import { useEffect, useRef, useState } from 'react';

interface NumberHighlightOptions {
  positiveClass?: string;
  negativeClass?: string;
  duration?: number;
}

const DEFAULT_DURATION = 300;
export const useNumberHighlight = (
  value: bigint | undefined = 0n,
  options: NumberHighlightOptions = {}
) => {
  const {
    positiveClass = 'text-contentGreen',
    negativeClass = 'text-contentRed',
    duration = DEFAULT_DURATION,
  } = options;

  const [className, setClassName] = useState<string>('');
  const prevValue = useRef<bigint>(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only update if the value has changed
    if (value !== prevValue.current) {
      // Determine if the new value is higher or lower
      const newClassName = value > prevValue.current ? positiveClass : negativeClass;
      setClassName(newClassName);

      // Set timeout to clear the className after specified duration
      timeoutRef.current = setTimeout(() => {
        setClassName('');
      }, duration);

      // Update the previous value
      prevValue.current = value;
    }

    // Cleanup timeout on unmount or when value changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, positiveClass, negativeClass, duration]);

  return className;
};
